import React, { useContext, useEffect, useState } from "react";
import "./course-documents.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import AppButtonComponent from "../../../../../components/form/app-button";
import { useTranslation } from "react-i18next";
import { services } from "../../../../../services";
import { showPopupToastMsg, showToastMsg } from "../../../../../helpers";
import { ConfirmDialog } from "../../../../../dialogs/confirm-dialog/confirm-dialog";
import { UserRoles } from "../../../../../types/userRoles";
import { GlobalContext } from "../../../../../store";
import { Skeleton } from "@material-ui/lab";

const CourseDocumentsComponent = ({
  loading,
  courseId,
  files,
  onDelete,
}: {
  loading: boolean;
  courseId: string;
  files: any[];
  onDelete: (id: number) => void;
}) => {
  const { t } = useTranslation();
  const { user } = useContext(GlobalContext);
  const [documents, setDocuments] = useState<any[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    setDocuments(files);
  }, [files]);

  const downloadFile = (url: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onCloseConfirmModal = (value: boolean, id: number) => {
    setShowConfirmModal(false);
    if (value) {
      deleteDocument(id);
    }
  };

  const deleteDocument = async (id: number) => {
    const response = await services.courses.deleteDocument(courseId, id);
    if (response.message === "SUCCESS") {
      showToastMsg("success", t("DOCUMENT_DELETED_SUCCESSFULLY"));
      setDocuments((prev: any) =>
        prev.filter((document: any) => document.id !== id),
      );
      onDelete(id);
    } else {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
  };

  return (
    <Box className="course__documents">
      <Box className="global__box__content p-0">
        <Box className="global__box__content__header">
          {loading ? (
            <Skeleton variant="text" width={200} height={20} />
          ) : (
            <Typography variant="subtitle1" className="font-weight-medium">
              {t("EDUCATION_PAGE.RELEVANT_DOCUMENTS")}
            </Typography>
          )}
        </Box>
        <Box className="global__box__content__body p-0">
          <Box className="items">
            {documents.map((item: any, index: number) => {
              return (
                <Box className="item" key={index}>
                  <Box className="info">
                    {loading ? (
                      <Skeleton variant="text" width={180} height={18} />
                    ) : (
                      <Typography variant="h3">{item.name}</Typography>
                    )}

                    {loading ? (
                      <Skeleton variant="text" width={160} height={16} />
                    ) : (
                      <Typography variant="body1" color="textSecondary">
                        {t("FILE_NAME")}: {item.file}
                      </Typography>
                    )}
                  </Box>
                  <Box className="actions">
                    {loading ? (
                      <Skeleton variant="text" width={100} height={30} />
                    ) : (
                      <AppButtonComponent
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => downloadFile(item.url)}
                      >
                        {t("DOWNLOAD")}
                      </AppButtonComponent>
                    )}
                    {UserRoles.FULL_ACCESS_ROLES.includes(user.role) &&
                      (loading ? (
                        <Skeleton variant="text" width={100} height={30} />
                      ) : (
                        <AppButtonComponent
                          variant="text"
                          color="secondary"
                          size="small"
                          onClick={() => setShowConfirmModal(true)}
                        >
                          {t("DELETE")}
                        </AppButtonComponent>
                      ))}
                    {showConfirmModal && (
                      <ConfirmDialog
                        onClose={(value: boolean) =>
                          onCloseConfirmModal(value, item.id)
                        }
                        dialogTitle={t("confirm delete")}
                        dialogBody={t(
                          "ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_DOCUMENT",
                        )}
                        confirmBtnText={t("delete")}
                        cancelBtn={true}
                        show={showConfirmModal}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CourseDocumentsComponent;
